import { Suspense, useEffect } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import ProtectedPageLayout from '@/pages/layouts/ProtectedPageLayout'
import PageNotFound from '@/pages/PageNotFound'
import DishesManagement from '@/pages/menu/DishesManagement'
import ToppingsManagement from '@/pages/menu/ToppingsManagement'
import EmployeeManagement from '@/pages/EmployeeManagement'
import EmployeeSchedule from '@/pages/EmployeeSchedule'
import { useAuth } from '@/features/auth/context/authProvider'
import LoadingPage from '@/pages/LoadingPage'
import MobileAppManagement from '@/pages/MobileAppManagement'

import MenuManagement from '@/pages/menu/MenuManagement'
import { useColorStore } from '@/features/mobile-app/store/useColorStore'
import TableCardsManagement from '@/pages/TableCardsManagement'
import IngredientsManagement from '@/pages/menu/IngredientsManagement'
import Dashboard from '@/pages/Dashboard'
import FeedbackPage from '@/pages/FeedbackPage'
import LocationChangeConfirmationModal from '@/shared/components/Modal/LocationChangeConfirmationModal/LocationChangeConfirmationModal'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import usePreloadQueries from '@/shared/hooks/usePreloadQueries'
import DataExtraction from '@/pages/menu/DataExtraction'
import RegisterDevicePage from '@/pages/RegisterDevicePage'
import SalesReports from '@/pages/SalesReports'
import FloorPlanPlanner from '@/pages/FloorPlanPlanner'
import Loyalty from '@/pages/Loyalty'
import MenuItemsSalesTablePage from '@/pages/SalesReports/MenuItemsSalesTablePage'
import InventorySalesTablePage from '@/pages/SalesReports/InventorySalesTablePage'
import { OnboardingProvider } from '@/features/onboarding'

type RestrictAccessProps = {
  children: React.ReactNode
}

function RestrictAccess({ children }: RestrictAccessProps) {
  const { userLoggedIn } = useAuth()
  const restaurantId = localStorage.getItem('restaurantId')

  return userLoggedIn !== null && userLoggedIn === true && restaurantId ? (
    <>{children}</>
  ) : (
    <Navigate to="/login" replace />
  )
}

function App() {
  const { isLoading } = usePreloadQueries()

  const isDirty = useFormDirtyStore(s => s.isDirty)

  useEffect(() => {
    useColorStore.getState().applyColors()
  }, [])

  if (isLoading) {
    return <LoadingPage />
  }

  return (
    <RestrictAccess>
      <OnboardingProvider>
        <ProtectedPageLayout>
          <Suspense fallback={<LoadingPage />}>
            <Outlet />
            <LocationChangeConfirmationModal when={isDirty} />
          </Suspense>
        </ProtectedPageLayout>
      </OnboardingProvider>
    </RestrictAccess>
  )
}

export const protectedRoutes = [
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: '/dashboard',
        element: <Dashboard />
      },
      {
        path: '/table-cards-management',
        element: <TableCardsManagement />
      },
      {
        path: '/floor-plan',
        element: <FloorPlanPlanner />
      },
      {
        path: '/employees-management',
        element: <EmployeeManagement />
      },
      {
        path: '/employees-schedule',
        element: <EmployeeSchedule />
      },
      {
        path: '/menu-management',
        element: <MenuManagement />,
        children: [
          { index: true, path: 'dishes', element: <DishesManagement /> },
          { path: 'toppings', element: <ToppingsManagement /> },
          { path: 'ingredients', element: <IngredientsManagement /> },
          { path: 'data-extraction', element: <DataExtraction /> }
        ]
      },
      {
        path: '/mobile-app-management',
        element: <MobileAppManagement />
      },
      {
        path: '/feedback',
        element: <FeedbackPage />
      },
      {
        path: '/loyalty',
        element: <Loyalty />
      },
      {
        path: '/register-device',
        element: <RegisterDevicePage />
      },
      {
        path: '/sales-reports',
        element: <SalesReports />,
        children: [
          {
            index: true,
            element: <Navigate to="/sales-reports/menu-items" replace />
          },
          { path: 'menu-items', element: <MenuItemsSalesTablePage /> },
          { path: 'inventory', element: <InventorySalesTablePage /> }
        ]
      }
    ]
  },
  { path: '/not-found', element: <PageNotFound /> },
  { path: '*', element: <Navigate to="/not-found" replace /> }
]
